import {
  Box,
  BoxProps,
  Popover,
  PopoverBody,
  PopoverContent,
  Text,
  PopoverFooter,
  PopoverHeader,
  HStack,
  Button,
  Stack,
  Portal,
  PopoverArrow,
  PopoverCloseButton,
  useDisclosure,
  PopoverAnchor,
  useColorModeValue,
} from '@chakra-ui/react';
import { DetectionDetailCard } from '@investigator/features/detections/components';
import {
  Detection,
  EntityDetectionTimelineItem,
  Maybe,
} from '@investigator/shared/generated/graphql';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { useLocation } from 'react-router';
import { ROUTES } from '@investigator/shared/constants';
import { useCallback } from 'react';
import { useEntitiesContext } from '@investigator/features/entities/context';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { SeverityBucket } from '@investigator/shared/types';

interface InlineBarPopoverProps {
  color: BoxProps['backgroundColor'];
  width: string;
  bucketLevel: SeverityBucket | undefined;
  totalDetections: number;
  detections: Maybe<Array<Maybe<Detection>>>;
  onFetchData?: (isOpen: boolean) => void;
  loading?: boolean;
  selectedEntityStart?: number;
  selectedEntityEnd?: number;
}

export const InlineBarPopover: React.FC<InlineBarPopoverProps> = ({
  color,
  width,
  bucketLevel,
  totalDetections,
  detections,
  onFetchData,
  loading = false,
  selectedEntityStart: propStart,
  selectedEntityEnd: propEnd,
}): JSX.Element => {
  const location = useLocation();
  const { isOpen, onToggle, onClose } = useDisclosure({
    onOpen: () => onFetchData?.(true),
    onClose: () => onFetchData?.(false),
  });
  const { start: urlStart, end: urlEnd } = useDateRangeQueryString();

  // Use props if provided, otherwise fall back to URL
  const start = propStart ?? urlStart;
  const end = propEnd ?? urlEnd;

  const { analysisModal } = useEntitiesContext();
  const buttonBg = useColorModeValue('gray.100', 'gray.700');
  const buttonColor = useColorModeValue('gray.800', 'gray.100');

  const mapDetectionToAlertRollup = (
    detection: Maybe<Detection>,
  ): EntityDetectionTimelineItem => {
    return {
      content_id: detection?.alert_entity.entity_id || '',
      alert_name: detection?.alert_info.alert_name || '',
      entity_name: detection?.alert_entity.entity_name || '',
      severity: detection?.rank.severity || 0,
      is_custom_severity: detection?.rank.is_custom_severity || false,
      last_seen: detection?.latest_start_timestamp || 0,
    };
  };

  const handleDetectionClick = useCallback(
    (detection: Maybe<Detection>) => {
      if (!detection?.detection_id) return;

      if (analysisModal.isOpen) {
        analysisModal.onClose();
      }
      // Per Mejji, this will open a new tab:
      window.open(
        ROUTES.detectionDetails(
          detection.detection_id,
          `?start=${start}&end=${end}`,
        ),
        '_blank',
      );
    },
    [start, end, analysisModal],
  );

  const handleAllDetectionsClick = useCallback(() => {
    if (analysisModal.isOpen) {
      analysisModal.onClose();
    }
    const currentParams = new URLSearchParams(location.search);
    currentParams.set('start', String(start));
    currentParams.set('end', String(end));
    currentParams.set(
      `${detections?.[0]?.alert_entity.entity_category ?? 'source'}_name`,
      detections?.[0]?.alert_entity.entity_name || '',
    );
    currentParams.set(
      'severity_range',
      bucketLevel !== undefined ? `${bucketLevel}` : '',
    );
    currentParams.set('status', 'open&status=closed');
    // Per Mejji, this will open a new tab:
    window.open(`${ROUTES.detections}?${currentParams.toString()}`, '_blank');
  }, [location, start, end, detections, bucketLevel, analysisModal]);

  return (
    <Popover placement="bottom-start" isOpen={isOpen} onClose={onClose}>
      <PopoverAnchor>
        <Box
          width={width}
          cursor="pointer"
          padding={'10px 0 10px 0'}
          onClick={(e) => {
            onToggle();
            e.stopPropagation();
          }}
          position="relative"
          overflow="visible"
        >
          <Box
            data-testid={`inline-bar-chart-segment-${bucketLevel}`}
            backgroundColor={color}
            height={1}
            borderRadius={2}
            transition="transform 0.2s ease-in-out"
            transform={isOpen ? 'scaleY(2.0)' : undefined}
            _hover={{
              transform: 'scaleY(2.0)',
              zIndex: 1,
            }}
          />
        </Box>
      </PopoverAnchor>
      <Portal>
        <PopoverContent
          w="400px"
          borderColor="layer.3"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverArrow />
          <PopoverCloseButton w={5} h={5} fontSize="xs" right={2} top={2} />
          <>
            <PopoverHeader>
              <HStack>
                <Text
                  data-testid="detection-severity-entity-name"
                  textStyle="body-md"
                  style={{ textTransform: 'capitalize' }}
                >
                  {(detections ?? [])[0]?.alert_entity.entity_name} |{' '}
                  {totalDetections} {bucketLevel} Severity
                  {totalDetections === 1 ? ' Detection' : ' Detections'}
                </Text>
              </HStack>
            </PopoverHeader>
            <PopoverBody>
              {loading ? (
                <GraphQLReqStatus loading={loading} />
              ) : (
                <Stack spacing={2} mt={2}>
                  {detections?.map(
                    (detection: Maybe<Detection>, index: number) => (
                      <DetectionDetailCard
                        key={detection?.detection_id || index}
                        condensed={true}
                        alertRollup={mapDetectionToAlertRollup(detection)}
                        onClick={() => handleDetectionClick(detection)}
                      />
                    ),
                  )}
                </Stack>
              )}
            </PopoverBody>
            <PopoverFooter border={0}>
              <Button
                w="full"
                variant="solid"
                rightIcon={<ArrowForwardIcon />}
                bg={buttonBg}
                color={buttonColor}
                onClick={handleAllDetectionsClick}
                data-testid="top-entity-table-popper-entity-navigation-button"
              >
                {totalDetections === 1
                  ? i18n.buttons.viewDetection
                  : i18n.buttons.viewDetections}
              </Button>
            </PopoverFooter>
          </>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
